// Типы и эндпоинты для админских новостей

// Тип для элемента списка новостей (согласно ответу бекенда)
export interface AdminNewsItem {
    alny_news_id: number;
    id?: number; // для совместимости, добавляется в transformResponse
    alny_news_video: 0 | 1;
    alny_news_video_url?: string | null;
    alny_news_time: number;
    poster?: string | null; // URL постера
    slug?: string | null;
    news_category_id?: number | null;
    is_published?: boolean;
    published_at?: string | null;
    show_poster?: boolean;
    translations?: Record<
        string,
        {
            title: string;
            excerpt?: string | null;
            body_html: string;
            seo_title?: string | null;
            seo_description?: string | null;
            seo_keywords?: string | null;
        }
    >;
    created_at?: string;
    updated_at?: string;
}

export interface AdminNewsListParams {
    page?: number;
    per_page?: number;
    news_category_id?: number;
    is_published?: boolean;
}

export interface AdminNewsListResponse {
    data: AdminNewsItem[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    meta?: { current_page: number; last_page: number };
}

export interface AdminNewsTranslationPayload {
    title: string;
    excerpt?: string | null;
    body_html: string;
    seo_title?: string | null;
    seo_description?: string | null;
    seo_keywords?: string | null;
}

export interface CreateAdminNewsRequest {
    slug?: string | null;
    news_category_id?: number | null;
    is_published?: boolean;
    published_at?: string | null;
    alny_news_video_url?: string | null;
    alny_news_poster?: File;
    show_poster?: boolean;
    translations: Record<string, AdminNewsTranslationPayload>;
}

export interface UpdateAdminNewsRequest
    extends Omit<CreateAdminNewsRequest, never> {}

// Типы для поддержки FormData
export type CreateAdminNewsRequestBody = CreateAdminNewsRequest | FormData;
export type UpdateAdminNewsRequestBody = UpdateAdminNewsRequest | FormData;

// Точная структура ответа детальной новости
export interface AdminNewsDetailResponse {
    alny_news_id: number;
    alny_news_video: 0 | 1;
    alny_news_video_url?: string | null;
    alny_news_time: number;
    poster?: string | null; // URL постера
    created_at?: string;
    updated_at?: string;
    slug?: string | null;
    news_category_id?: number | null;
    is_published?: boolean;
    published_at?: string | null;
    show_poster?: boolean;
    translations?: CreateAdminNewsRequest["translations"];
}

export const newsEndpoints = {
    getAdminNews: {
        query: (params: AdminNewsListParams) => ({
            url: "/admin/news",
            params,
        }),
        transformResponse: (
            response: any,
            meta: any,
            arg: AdminNewsListParams
        ) => {
            const items = (response.data || []).map((item: any) => ({
                ...item,
                id: item.alny_news_id,
            }));
            return {
                data: items,
                current_page: response.current_page || 1,
                last_page: response.last_page || 1,
                per_page: response.per_page || 20,
                total: response.total || 0,
                meta: {
                    current_page: response.current_page || 1,
                    last_page: response.last_page || 1,
                },
            };
        },
        providesTags: () => [{ type: "AdminNews" as const, id: "LIST" }],
    },

    getAdminNewsItem: {
        query: (id: number) => `/admin/news/${id}`,
        transformResponse: (response: any) => ({ ...response, id: response.alny_news_id }),
        providesTags: (result: any, error: any, id: number) => [
            { type: "AdminNews" as const, id: id },
        ],
    },

    createAdminNews: {
        query: (data: CreateAdminNewsRequestBody) => {
            // Если это FormData, не устанавливаем Content-Type (браузер установит автоматически)
            const isFormData = data instanceof FormData;
            return {
                url: "/admin/news",
                method: "POST",
                body: data,
                // RTK Query автоматически определяет FormData и не устанавливает Content-Type
                // Но явно указываем, что это multipart/form-data
                ...(isFormData && {
                    formData: true,
                }),
            };
        },
        invalidatesTags: () => [{ type: "AdminNews" as const, id: "LIST" }],
    },

    updateAdminNews: {
        query: (args: { id: number; data: UpdateAdminNewsRequestBody }) => {
            const isFormData = args.data instanceof FormData;
            // Для FormData используем POST с _method=PUT (Laravel не поддерживает PUT с multipart/form-data)
            if (isFormData) {
                (args.data as FormData).append("_method", "PUT");
                return {
                    url: `/admin/news/${args.id}`,
                    method: "POST",
                    body: args.data,
                };
            }
            // Для обычных JSON запросов используем PUT
            return {
                url: `/admin/news/${args.id}`,
                method: "PUT",
                body: args.data,
            };
        },
        invalidatesTags: (result: any, error: any, args: { id: number }) => [
            { type: "AdminNews" as const, id: "LIST" },
            { type: "AdminNews" as const, id: args.id },
        ],
    },

    deleteAdminNews: {
        query: (id: number) => ({
            url: `/admin/news/${id}`,
            method: "DELETE",
        }),
        invalidatesTags: (result: any, error: any, id: number) => [
            { type: "AdminNews" as const, id: "LIST" },
            { type: "AdminNews" as const, id: id },
        ],
    },

    uploadAdminNewsImage: {
        query: (file: File) => {
            const form = new FormData();
            form.append("image", file);
            return {
                url: "/admin/news/images",
                method: "POST",
                body: form,
            };
        },
    },

    getAdminNewsCategories: {
        query: () => ({ url: "/admin/news-categories" }),
    },
    createAdminNewsCategory: {
        query: (data: any) => ({ url: "/admin/news-categories", method: "POST", body: data }),
    },
    updateAdminNewsCategory: {
        query: (args: { id: number; data: any }) => ({
            url: `/admin/news-categories/${args.id}`,
            method: "PUT",
            body: args.data,
        }),
    },
    deleteAdminNewsCategory: {
        query: (id: number) => ({ url: `/admin/news-categories/${id}`, method: "DELETE" }),
    },
};

export const newsTags = ["AdminNews"] as const;
